import type { ReactNode } from "react";
import { useTranslate } from "ra-core";

import type { PublicQuote, PublicQuoteDetail } from "../types";
import { QrCode } from "./QrCode";
import { buildQrMessage, whatsappUrl } from "./quoteWhatsapp";
import { formatIsoDate, formatMxn } from "./quoterFormat";

/**
 * Almikia Epic EQ2 (TASK-101): the two printable sheets of the public quote.
 * Almikia brand only (navy / cream, no development logo, isometric, floor plan
 * or photo). `.quote-sheet` is paged in `src/index.css` (print: one sheet per
 * page, A4 or Letter, app chrome hidden by the `print:hidden` buttons).
 */

const SHEET_CLASS =
  "quote-sheet rounded-xl bg-card p-5 text-base shadow-[0_1px_2px_rgba(39,44,87,.06),0_8px_24px_-12px_rgba(39,44,87,.18)] sm:p-8 print:rounded-none print:p-0 print:text-[9pt] print:leading-snug print:shadow-none";

const SQUARE_METERS = (value: number) =>
  `${value.toLocaleString("es-MX", { maximumFractionDigits: 2 })} m²`;

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="space-y-1 print:break-inside-avoid">
    <h3 className="text-base font-bold text-brand-navy print:text-[10pt]">
      {title}
    </h3>
    {children}
  </section>
);

const Row = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
}) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-brand-navy-10 py-1 print:py-0.5">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className={`text-right tabular-nums ${strong ? "font-bold" : ""}`}>
      {value}
    </dd>
  </div>
);

const SheetHeader = ({
  detail,
  subtitle,
}: {
  detail: PublicQuoteDetail;
  subtitle: string;
}) => {
  const translate = useTranslate();
  return (
    <header className="flex items-start justify-between gap-4 border-b-2 border-brand-navy pb-2">
      <p className="text-xl font-bold text-brand-navy print:text-[14pt]">
        {translate("crm.quoter.public.brand")}
      </p>
      <div className="text-right text-sm tabular-nums print:text-[8pt]">
        <p className="font-bold">
          {translate("crm.quoter.public.sheet.folio", { folio: detail.folio })}
        </p>
        <p>{subtitle}</p>
        <p>
          {translate("crm.quoter.public.sheet.issued", {
            date: formatIsoDate(detail.issued_on),
          })}
        </p>
        <p>
          {translate("crm.quoter.public.sheet.valid", {
            date: formatIsoDate(detail.valid_until, "long"),
          })}
        </p>
      </div>
    </header>
  );
};

/** The advisor card with the "Escanee y hablamos" QR to her WhatsApp. */
const AdvisorCard = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  const advisor = quote.detail?.advisor;
  if (!advisor) return null;
  const qrUrl = advisor.whatsapp
    ? whatsappUrl(advisor.whatsapp, buildQrMessage(quote, translate))
    : null;
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg bg-brand-navy p-4 text-white print:break-inside-avoid print:p-2">
      <div className="space-y-0.5">
        <p className="text-sm text-white/70 print:text-[8pt]">
          {translate("crm.quoter.public.sheet.advisor")}
        </p>
        <p className="text-lg font-bold print:text-[11pt]">{advisor.name}</p>
        {advisor.whatsapp ? (
          <p className="tabular-nums">
            {translate("crm.quoter.public.sheet.whatsapp")} {advisor.whatsapp}
          </p>
        ) : null}
        <p className="text-sm text-white/70 print:text-[8pt]">
          {translate("crm.quoter.public.sheet.advisor_promise")}
        </p>
      </div>
      {qrUrl ? (
        <div className="flex w-24 shrink-0 flex-col items-center gap-1 print:w-20">
          <QrCode
            value={qrUrl}
            label={translate("crm.quoter.public.sheet.qr_label")}
            className="w-full rounded bg-white"
          />
          <span className="text-center text-sm print:text-[8pt]">
            {translate("crm.quoter.public.sheet.qr_caption")}
          </span>
        </div>
      ) : null}
    </div>
  );
};

/** Sheet 1: the unit, the investment, the models, the scarcity and the advisor. */
export const QuoteSheetOne = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  const detail = quote.detail!;
  const { unit, prices } = detail;
  return (
    <article className={SHEET_CLASS} data-testid="quote-sheet-1">
      <SheetHeader detail={detail} subtitle={quote.project_name} />
      <p className="mt-3 text-lg print:text-[11pt]">
        {translate("crm.quoter.public.prepared_for")}{" "}
        <strong className="text-brand-navy">{quote.client_first_name}</strong>
      </p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2 print:grid-cols-2 print:gap-3">
        <div className="space-y-3">
          <Section title={translate("crm.quoter.public.sheet.residence")}>
            <dl>
              {unit.model_name ? (
                <Row
                  label={translate("crm.quoter.public.sheet.model")}
                  value={unit.model_name}
                />
              ) : null}
              {unit.program ? (
                <Row
                  label={translate("crm.quoter.public.sheet.program")}
                  value={unit.program}
                />
              ) : null}
              {unit.m2_per_floor != null ? (
                <Row
                  label={translate("crm.quoter.public.sheet.per_floor")}
                  value={SQUARE_METERS(unit.m2_per_floor)}
                />
              ) : null}
              {unit.garage ? (
                <Row
                  label={translate("crm.quoter.public.sheet.garage")}
                  value={unit.garage}
                />
              ) : null}
              {unit.total_construction != null ? (
                <Row
                  strong
                  label={translate("crm.quoter.public.sheet.construction")}
                  value={SQUARE_METERS(unit.total_construction)}
                />
              ) : null}
            </dl>
          </Section>
          <Section title={translate("crm.quoter.public.sheet.unit")}>
            <dl>
              <Row
                label={translate("crm.quoter.public.sheet.key")}
                value={quote.unit_identifier}
              />
              {unit.block_label ? (
                <Row
                  label={translate("crm.quoter.public.sheet.block")}
                  value={unit.block_label}
                />
              ) : null}
              {unit.stage != null ? (
                <Row
                  label={translate("crm.quoter.public.sheet.stage")}
                  value={unit.stage}
                />
              ) : null}
              {unit.street ? (
                <Row
                  label={translate("crm.quoter.public.sheet.street")}
                  value={unit.street}
                />
              ) : null}
              {unit.surface_m2 != null ? (
                <Row
                  label={translate("crm.quoter.public.sheet.surface")}
                  value={SQUARE_METERS(unit.surface_m2)}
                />
              ) : null}
              {unit.measures ? (
                <Row
                  label={translate("crm.quoter.public.sheet.measures")}
                  value={unit.measures}
                />
              ) : null}
              {prices.excess_m2 > 0 ? (
                <Row
                  label={translate("crm.quoter.public.sheet.excess_land")}
                  value={SQUARE_METERS(prices.excess_m2)}
                />
              ) : null}
              {unit.orientation ? (
                <Row
                  label={translate("crm.quoter.public.sheet.orientation")}
                  value={unit.orientation}
                />
              ) : null}
            </dl>
            {unit.lot_type ? (
              <p className="rounded-md bg-brand-navy-10 p-2 print:p-1">
                <strong>{unit.lot_type}.</strong> {unit.lot_type_description}
              </p>
            ) : null}
          </Section>
        </div>
        <div className="space-y-3">
          <Section title={translate("crm.quoter.public.sheet.investment")}>
            <dl>
              <Row
                label={translate("crm.quoter.public.sheet.list_price", {
                  list: detail.list_number,
                })}
                value={formatMxn(prices.base)}
              />
              {prices.excess > 0 ? (
                <Row
                  label={translate("crm.quoter.public.sheet.excess_price", {
                    m2: SQUARE_METERS(prices.excess_m2),
                  })}
                  value={formatMxn(prices.excess)}
                />
              ) : null}
              {prices.roof > 0 ? (
                <Row
                  label={translate(
                    unit.roof_included
                      ? "crm.quoter.public.sheet.roof_included"
                      : "crm.quoter.public.sheet.roof",
                  )}
                  value={formatMxn(prices.roof)}
                />
              ) : null}
              {prices.extras > 0 ? (
                <Row
                  label={translate("crm.quoter.public.sheet.extras")}
                  value={formatMxn(prices.extras)}
                />
              ) : null}
              {prices.discount > 0 ? (
                <Row
                  label={translate("crm.quoter.public.sheet.discount", {
                    scheme: detail.scheme_name,
                  })}
                  value={`− ${formatMxn(prices.discount)}`}
                />
              ) : null}
            </dl>
            <div className="flex items-baseline justify-between rounded-md bg-brand-navy px-3 py-2 text-white print:py-1">
              <span className="font-bold">
                {translate("crm.quoter.public.sheet.final_price")}
              </span>
              <span className="text-xl font-bold tabular-nums print:text-[13pt]">
                {formatMxn(prices.final)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground print:text-[8pt]">
              {detail.scheme_name} ·{" "}
              {`${detail.plan.down_pct} / ${detail.plan.monthly_pct} / ${detail.plan.deed_pct}`}
            </p>
          </Section>
          <Section
            title={translate("crm.quoter.public.sheet.models", {
              list: detail.list_number,
            })}
          >
            <table className="w-full text-sm tabular-nums print:text-[8pt]">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-0.5 font-medium">
                    {translate("crm.quoter.public.sheet.model")}
                  </th>
                  <th className="py-0.5 font-medium">
                    {translate("crm.quoter.public.sheet.construction")}
                  </th>
                  <th className="py-0.5 font-medium">
                    {translate("crm.quoter.public.sheet.program")}
                  </th>
                  <th className="py-0.5 text-right font-medium">
                    {translate("crm.quoter.public.sheet.from")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.models.map((model) => (
                  <tr
                    key={model.code}
                    className={
                      model.code === unit.model_code
                        ? "bg-brand-navy-10 font-medium"
                        : ""
                    }
                  >
                    <td className="py-0.5">{model.name}</td>
                    <td className="py-0.5">
                      {model.total_construction != null
                        ? SQUARE_METERS(model.total_construction)
                        : "—"}
                    </td>
                    <td className="py-0.5">{model.program ?? "—"}</td>
                    <td className="py-0.5 text-right">
                      {model.price_from != null
                        ? formatMxn(model.price_from)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
          <p
            className="font-bold text-brand-magenta"
            data-testid="quote-scarcity"
          >
            {detail.scarcity.in_stage != null
              ? translate("crm.quoter.public.sheet.scarcity", {
                  in_stage: detail.scarcity.in_stage,
                  in_development: detail.scarcity.in_development,
                })
              : translate("crm.quoter.public.sheet.scarcity_development", {
                  in_development: detail.scarcity.in_development,
                })}
          </p>
          <AdvisorCard quote={quote} />
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground print:text-[7pt]">
        {translate("crm.quoter.public.sheet.footer", {
          apartado: formatMxn(prices.apartado),
        })}
      </p>
      <p className="mt-1 text-right text-sm text-muted-foreground print:text-[7pt]">
        {translate("crm.quoter.public.sheet.page", { page: 1 })}
      </p>
    </article>
  );
};

/** Sheet 2: the payment calendar (dates as the engine froze them). */
export const QuoteSheetTwo = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  const detail = quote.detail!;
  const { prices } = detail;
  return (
    <article className={SHEET_CLASS} data-testid="quote-sheet-2">
      <SheetHeader
        detail={detail}
        subtitle={translate("crm.quoter.public.sheet.plan_for", {
          name: quote.client_first_name,
        })}
      />
      <p className="mt-3 text-lg print:text-[11pt]">
        {translate("crm.quoter.public.plan_intro")}
      </p>
      <p className="mt-1 text-muted-foreground">
        {translate("crm.quoter.public.sheet.plan_note", {
          apartado: formatMxn(prices.apartado),
          date: formatIsoDate(detail.valid_until, "long"),
        })}
      </p>
      <div className="mt-3 space-y-2 rounded-lg border border-brand-navy-40 p-4 print:p-2">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <p className="text-lg font-bold text-brand-navy print:text-[11pt]">
              {detail.scheme_name}
            </p>
            <p className="text-sm text-muted-foreground tabular-nums print:text-[8pt]">
              {`${detail.plan.down_pct} / ${detail.plan.monthly_pct} / ${detail.plan.deed_pct}`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold tabular-nums print:text-[13pt]">
              {formatMxn(prices.final)}
            </p>
            <p className="text-sm text-muted-foreground print:text-[8pt]">
              {prices.discount_pct > 0
                ? translate("crm.quoter.public.sheet.includes_discount", {
                    pct: prices.discount_pct,
                  })
                : translate("crm.quoter.public.sheet.list_price_only")}
            </p>
          </div>
        </div>
        <table
          className="w-full text-sm tabular-nums print:text-[8.5pt]"
          data-testid="quote-calendar"
        >
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 font-medium">
                {translate("crm.quoter.public.sheet.date")}
              </th>
              <th className="py-1 font-medium">
                {translate("crm.quoter.schedule.concept")}
              </th>
              <th className="py-1 text-right font-medium">
                {translate("crm.quoter.schedule.amount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {detail.calendar.map((row) => (
              <tr key={row.seq} className="border-t border-brand-navy-10">
                <td className="py-1 print:py-0.5">
                  {formatIsoDate(row.due_date)}
                </td>
                <td className="py-1 print:py-0.5">{row.concept}</td>
                <td className="py-1 text-right print:py-0.5">
                  {formatMxn(row.amount)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-brand-navy font-bold">
              <td className="py-1" />
              <td className="py-1">
                {translate("crm.quoter.public.sheet.total")}
              </td>
              <td className="py-1 text-right">{formatMxn(prices.final)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <AdvisorCard quote={quote} />
      </div>
      <p className="mt-3 text-right text-sm text-muted-foreground print:text-[7pt]">
        {translate("crm.quoter.public.sheet.page", { page: 2 })}
      </p>
    </article>
  );
};
